import { capture } from "@/lib/posthog";
import React, { useEffect } from "react";
import { Dialog, DialogCloseButton } from "@/components/ui/dialog";
import { ConnectMcpPanel } from "@/components/ConnectMcpPanel";
import { connectMcpTestIds as ids } from "@/testids/connectMcpTestIds";
import "./connect-mcp-modal.css";

/** Where the dialog was opened from, for the distribution funnel. */
// "agent-advisory": the connector-build notice the orchestrator writes when a
// build starts (services/agent/.../tools/connector-notice.ts). Counted apart
// from the rest because it is the only entry point the AGENT opens rather
// than the user going looking — the one number that says whether advising
// mid-build actually moves people to a coding agent.
export type ConnectMcpSource = "topbar" | "home" | "guide" | "onboarding" | "agent-advisory";

export interface ConnectMcpModalProps {
  open: boolean;
  onClose: () => void;
  source: ConnectMcpSource;
}

/**
 * The Connect dialog: chrome around ConnectMcpPanel, which owns the actual
 * picker and is shared with Settings > MCP server.
 */
export function ConnectMcpModal({
  open,
  onClose,
  source,
}: ConnectMcpModalProps): React.ReactElement {
  // How fastn reaches surfaces that are not fastn. Worth counting separately
  // from the rest of the dashboard: this is distribution, not feature usage,
  // and its denominator is "people who found it" rather than "people who
  // logged in". Call sites mount this only while open, so a mount IS an open.
  // `source` is a prop because several surfaces open this dialog — it used to
  // be hardcoded to "topbar", which made the other entry points invisible.
  useEffect(() => {
    capture("mcp_connect_opened", { source, role: null });
  }, [source]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      labelledBy={ids.modalTitle}
      className="claude-modal"
      data-testid={ids.modal}
      overlayTestId={ids.modalOverlay}
    >
      <div className="clHead">
        <div className="clHeadBody">
          <h2 className="clTitle" id={ids.modalTitle} data-testid={ids.modalTitle}>
            Connect fastn to your favourite agent
          </h2>
          <p className="clSub" data-testid={ids.modalDescription}>
            Add fastn as an MCP server and your agent can build integrations for you, right in
            the chat.
          </p>
        </div>
        <DialogCloseButton onClick={onClose} data-testid={ids.modalCloseButton} />
      </div>

      <div className="clBody mcpPanel">
        <ConnectMcpPanel />
      </div>
    </Dialog>
  );
}
